import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import { youtubeWatchUrlFromWatchUrl } from "../../utils/youtubeVideoId";
import type { TreemapSubtopicSelection } from "./subtopicChunkMatch";

export type SubtopicChunkListItem = {
  chunkIndex?: number;
  startTime: number;
  endTime: number;
  text: string;
  contentId?: string;
  contentTitle?: string;
  url?: string;
  source?: string;
  platformPostId?: string;
  views?: number;
  interactions?: number;
  liveViewersAtChunk?: number | null;
  collaborators?: string[];
  taggedAccounts?: string[];
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type SubtopicChunksPanelProps = {
  selection: TreemapSubtopicSelection | null;
  chunks: SubtopicChunkListItem[];
  loading?: boolean;
  onClose: () => void;
};

const SubtopicChunksPanel: React.FC<SubtopicChunksPanelProps> = ({
  selection,
  chunks,
  loading = false,
  onClose,
}) => {
  const open = selection != null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {selection ? (
          <>
            {selection.subtopicDisplay}
            <Typography variant="caption" color="text.secondary" display="block">
              {selection.topicSlug} / {selection.subtopicSlug}
            </Typography>
          </>
        ) : (
          "Subtopic chunks"
        )}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Typography variant="body2" color="text.secondary">
            Loading chunks…
          </Typography>
        )}
        {!loading && chunks.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No accepted chunks found for this subtopic.
          </Typography>
        )}
        {!loading && chunks.length > 0 && (
          <Stack spacing={2}>
            {chunks.map((chunk, index) => {
              const ytUrl =
                chunk.source === "youtube" && chunk.url
                  ? youtubeWatchUrlFromWatchUrl(chunk.url, chunk.startTime)
                  : null;
              const key = `${chunk.contentId ?? "episode"}:${chunk.chunkIndex ?? index}`;

              return (
                <Box
                  key={key}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      {chunk.contentTitle && (
                        <Typography variant="subtitle2">{chunk.contentTitle}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(chunk.startTime)} – {formatTime(chunk.endTime)}
                        {chunk.chunkIndex != null ? ` · chunk ${chunk.chunkIndex + 1}` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {ytUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<YouTubeIcon />}
                          href={ytUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          YouTube
                        </Button>
                      )}
                      {chunk.url && !ytUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          href={chunk.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {chunk.text}
                  </Typography>
                  {chunk.contentId && (
                    <Button
                      component={RouterLink}
                      to={`/review/content/${chunk.contentId}/topics`}
                      size="small"
                      sx={{ mt: 0.5, p: 0, minWidth: 0 }}
                    >
                      Episode topics
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubtopicChunksPanel;
